import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";
import HomeVisitRequest from "./HomeVisitRequest";
import { formatIstDateTime } from "../../utils/datetime";

const HV_BASES = (ApiBaseUrl) => [
  `${ApiBaseUrl}/customer/home-visit`,
  `${ApiBaseUrl}/api/home-visit`,
];

async function hvPost(ApiBaseUrl, path, body) {
  let lastErr;
  for (const base of HV_BASES(ApiBaseUrl)) {
    try {
      const r = await axios.post(`${base}${path}`, body);
      if (r.data?.Result === true || r.data?.ResponseCode === 200) return r.data;
      lastErr = r.data;
    } catch (e) {
      lastErr = e?.response?.data || e;
    }
  }
  return lastErr;
}

function parseBookings(data) {
  const list = data?.bookings || data?.data || data?.list || data?.rows || data?.items;
  return Array.isArray(list) ? list : [];
}

export default function HomeVisitBookings() {
  const { ApiBaseUrl, setIsLoginShow } = useContext(MyContext);
  const navigate = useNavigate();
  const Userid = secureLocalStorage.getItem("Userid");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);
  const [showBook, setShowBook] = useState(false);

  const load = async () => {
    if (!Userid) {
      setIsLoginShow(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    const data = await hvPost(ApiBaseUrl, "/bookings/list", { id: Userid });
    setLoading(false);
    if (data?.Result === true || data?.ResponseCode === 200) {
      setBookings(parseBookings(data));
      setApiMissing(false);
    } else {
      setBookings([]);
      setApiMissing(true);
    }
  };

  useEffect(() => {
    load();
  }, [Userid, ApiBaseUrl]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this home visit?")) return;
    const data = await hvPost(ApiBaseUrl, `/bookings/${id}/cancel`, {
      id: Userid,
      reason: "changed plans",
    });
    if (data?.Result === true || data?.ResponseCode === 200) {
      showToast({ title: data.message || "Cancelled", id: "success" });
      load();
    } else {
      showToast({ title: data?.message || "Could not cancel", id: "error" });
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-rm-ink">My home visits</h1>
          <p className="text-gray-600 text-sm">Doctor-at-home requests</p>
        </div>
        <button
          className="bg-[#0d9488] text-white px-4 py-2 rounded-lg"
          onClick={() => setShowBook(true)}
        >
          Book visit
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-10">Loading…</p>
      ) : apiMissing ? (
        <div className="text-center py-12 border rounded-xl bg-white">
          <p className="font-medium text-lg">Home visit API not enabled yet</p>
          <p className="text-gray-500 mt-1 mb-4">
            You can still book nurses & caregivers via Home Care.
          </p>
          <button
            className="bg-rm-primary text-white px-4 py-2 rounded-lg"
            onClick={() => navigate("/home-care/bookings")}
          >
            Go to Home Care
          </button>
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-12 border rounded-xl bg-white">
          <p className="font-medium text-lg">No home visits yet</p>
          <button
            className="mt-4 bg-[#0d9488] text-white px-4 py-2 rounded-lg"
            onClick={() => setShowBook(true)}
          >
            Request a visit
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => {
            const id = b.id || b.booking_id;
            const status = (b.status || b.state || "").toString().replace(/_/g, " ");
            const address =
              b.address ||
              b.address_line ||
              b.address_json?.line1 ||
              (typeof b.address_json === "string" ? b.address_json : "") ||
              "";
            return (
              <div key={id} className="border rounded-xl p-4 bg-white flex justify-between gap-3">
                <div>
                  <p className="text-xs text-rm-primary">#{id}</p>
                  <h3 className="font-semibold capitalize">{status || "Active"}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {b.doctor_name || b.provider_name || "Finding doctor…"}
                  </p>
                  {address && <p className="text-xs text-gray-500 mt-1">{address}</p>}
                  {b.eta_minutes != null && (
                    <p className="text-xs text-rm-primary font-semibold mt-1">
                      ETA {b.eta_minutes} min{b.distance_km != null ? ` · ${b.distance_km} km` : ""}
                    </p>
                  )}
                  {b.map_url && (
                    <a href={b.map_url} target="_blank" rel="noopener noreferrer" className="text-xs text-rm-primary underline mt-1 inline-block">
                      Open map
                    </a>
                  )}
                  {(b.created_at || b.createdAt) && (
                    <p className="text-xs text-gray-400 mt-1">{formatIstDateTime(b.created_at || b.createdAt) || b.created_at || b.createdAt}</p>
                  )}
                </div>
                {status && !/cancel|complete|done/i.test(status) && (
                  <button
                    className="text-red-600 text-sm self-start"
                    onClick={() => cancel(id)}
                  >
                    Cancel
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showBook && (
        <HomeVisitRequest onClose={() => { setShowBook(false); load(); }} />
      )}
    </div>
  );
}
